/**
 * VisualScale — horizontal traffic-light scale with a pointer at the
 * current value. Used to translate raw metric numbers (NDVI 0.59,
 * Bodenfeuchte 0.29 m³/m³, ET₀ 17 mm, T 16 °C) into a one-glance reading.
 *
 * Usage:
 *   <VisualScale
 *     icon={Plant}
 *     label="Vegetationsgrün (NDVI)"
 *     value={0.59}
 *     min={0} max={1}
 *     zones={[
 *       {to: 0.3, color: "#FF5500", label: "schwach"},
 *       {to: 0.5, color: "#FFC24B", label: "moderat"},
 *       {to: 1.0, color: "#00FA9A", label: "kräftig"},
 *     ]}
 *     formatValue={(v) => v.toFixed(2)}
 *     klartext="Pflanzen sind gut grün und wachsen normal."
 *     testid="scale-ndvi"
 *   />
 */
import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CaretDown } from "@phosphor-icons/react";

export default function VisualScale({
  icon: Icon, label, value, min = 0, max = 1, unit = "",
  zones = [], formatValue, klartext, source, details, testid,
}) {
  const v = typeof value === "number" ? value : null;
  const clamped = v == null ? null : Math.max(min, Math.min(max, v));
  const pct = clamped == null ? null : ((clamped - min) / (max - min)) * 100;

  // Build a multi-stop linear gradient from the zone list.
  const gradient = useMemo(() => {
    if (!zones.length) return "linear-gradient(to right, #FFC24B, #00FA9A)";
    const parts = [];
    let last = min;
    zones.forEach((z) => {
      const startPct = ((last - min) / (max - min)) * 100;
      const endPct = ((z.to - min) / (max - min)) * 100;
      parts.push(`${z.color} ${startPct}%`, `${z.color} ${endPct}%`);
      last = z.to;
    });
    return `linear-gradient(to right, ${parts.join(", ")})`;
  }, [zones, min, max]);

  const activeZone = useMemo(() => {
    if (v == null) return null;
    return zones.find((z) => v <= z.to) || zones[zones.length - 1];
  }, [v, zones]);

  const display = v == null
    ? "—"
    : (formatValue ? formatValue(v) : v.toFixed(2));

  const [open, setOpen] = useState(false);

  return (
    <div className="bg-[#121212] border border-white/15 p-4 sm:p-5 rounded-sm" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && <Icon size={16} weight="fill" className="text-[#00FA9A] shrink-0" />}
          <span className="text-[11px] font-mono uppercase tracking-widest text-zinc-400 leading-tight">{label}</span>
        </div>
        {activeZone && (
          <span
            className="text-[10px] uppercase tracking-widest font-mono font-semibold px-2 py-0.5 rounded-sm shrink-0"
            style={{ background: activeZone.color, color: "#FFFFFF" }}
          >
            {activeZone.label}
          </span>
        )}
      </div>

      <div className="flex items-baseline gap-2 mb-3">
        <span className="bb-metric text-3xl sm:text-4xl text-white tracking-tighter leading-none">{display}</span>
        {unit && <span className="text-xs font-mono text-zinc-400">{unit}</span>}
      </div>

      {/* Scale */}
      <div className="relative h-2.5 rounded-full overflow-visible" style={{ background: gradient }}>
        {/* Pointer */}
        {pct != null && (
          <motion.div
            className="absolute -top-1.5 -translate-x-1/2"
            initial={{ left: "0%", opacity: 0 }}
            animate={{ left: `${pct}%`, opacity: 1 }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }}
          >
            <div className="w-[3px] h-[22px] bg-white/10 rounded-full" />
            <div className="w-2 h-2 rounded-full bg-white/10 -mt-1 mx-auto" />
          </motion.div>
        )}
      </div>

      {/* Zone tick labels */}
      <div className="mt-1.5 flex justify-between text-[9px] font-mono uppercase tracking-widest text-zinc-500">
        <span>{min}{unit && ` ${unit}`}</span>
        <span>{max}{unit && ` ${unit}`}</span>
      </div>

      {/* Klartext */}
      {klartext && (
        <p className="mt-3 text-sm text-white leading-snug" data-testid={testid && `${testid}-klartext`}>
          {klartext}
        </p>
      )}
      {source && (
        <p className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">{source}</p>
      )}

      {details && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="mt-3 w-full flex items-center justify-between gap-2 text-[10px] font-mono uppercase tracking-[0.22em] text-[#00FA9A] hover:text-[#00FA9A] py-2 border-t border-white/15"
            data-testid={testid && `${testid}-toggle`}
          >
            <span>{open ? "Weniger anzeigen" : "Mehr verstehen"}</span>
            <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
              <CaretDown size={12} weight="bold" />
            </motion.span>
          </button>
          <AnimatePresence initial={false}>
            {open && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <div className="pt-2 pb-1 text-[13px] text-white/85 leading-relaxed space-y-2" data-testid={testid && `${testid}-details`}>
                  {details}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </div>
  );
}
